import { Camera, Check, LoaderCircle, LogIn, TriangleAlert, Upload, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { getCurrentMemberAuthEmail, signInWithGoogle, updateCurrentMemberMobileContact, updateCurrentMemberProfile, uploadCurrentMemberAvatar } from "./live-data";
import { AVATAR_SIDE, MAX_AVATAR_BYTES, resizeProfilePhoto, type ResizedProfilePhoto } from "./profile-photo";
import { toSabahMalay, type InterfaceLanguage } from "./i18n";

type Language = InterfaceLanguage;

export type EditableProfile = {
  firstName: string;
  lastName: string;
  email: string;
  district: string;
  avatarUrl: string;
  mobileNumber: string;
  mobileNotificationsConsent: boolean;
};

type ProfileEditorProps = {
  member: EditableProfile;
  districts: string[];
  language?: Language;
  onClose: () => void;
  onSaved: (patch: Pick<EditableProfile, "firstName" | "lastName" | "district" | "avatarUrl" | "mobileNumber" | "mobileNotificationsConsent">) => void;
  notify: (message: string) => void;
};

function formatBytes(bytes: number) {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function ProfileEditor({ member, districts, language = "en", onClose, onSaved, notify }: ProfileEditorProps) {
  const t = (ms: string, en: string) => language === "en" ? en : language === "sabah" ? toSabahMalay(ms) : ms;
  const [firstName, setFirstName] = useState(member.firstName);
  const [lastName, setLastName] = useState(member.lastName);
  const [district, setDistrict] = useState(member.district);
  const [mobileNumber, setMobileNumber] = useState(member.mobileNumber);
  const [mobileNotificationsConsent, setMobileNotificationsConsent] = useState(member.mobileNotificationsConsent);
  const [photo, setPhoto] = useState<ResizedProfilePhoto | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => () => {
    if (photo) URL.revokeObjectURL(photo.previewUrl);
  }, [photo]);

  useEffect(() => {
    let active = true;
    void getCurrentMemberAuthEmail().then((email) => {
      if (!active) return;
      setSignedInEmail(email);
      setAuthChecked(true);
    });
    return () => { active = false; };
  }, []);

  const profileEmail = member.email.trim().toLowerCase();
  const accountMismatch = authChecked && Boolean(signedInEmail) && signedInEmail !== profileEmail;

  const choosePhoto = async (file?: File) => {
    if (!file) return;
    setError("");
    setPhotoBusy(true);
    try {
      const resized = await resizeProfilePhoto(file);
      setPhoto((current) => {
        if (current) URL.revokeObjectURL(current.previewUrl);
        return resized;
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Foto tidak dapat diproses.", "The photo could not be processed."));
    } finally {
      setPhotoBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const cleanFirstName = firstName.trim();
    const cleanLastName = lastName.trim();
    if (!cleanFirstName || !cleanLastName || !district) return;

    setSaving(true);
    setError("");
    try {
      const activeEmail = await getCurrentMemberAuthEmail();
      if (!activeEmail) throw new Error(t("Log masuk dengan Google sekali lagi sebelum mengemas kini profil anda.", "Sign in with Google again before updating your profile."));
      if (activeEmail !== profileEmail) {
        throw new Error(t(`Anda sedang log masuk sebagai ${activeEmail}. Tukar kepada ${profileEmail} untuk mengemas kini profil ini.`, `You are signed in as ${activeEmail}. Switch to ${profileEmail} to update this profile.`));
      }
      const avatarUrl = photo ? await uploadCurrentMemberAvatar(photo.blob, photo.contentType) : member.avatarUrl;
      await updateCurrentMemberProfile({ firstName: cleanFirstName, lastName: cleanLastName, district, avatarUrl });
      const normalizedMobileNumber = await updateCurrentMemberMobileContact(mobileNumber, mobileNotificationsConsent);
      onSaved({ firstName: cleanFirstName, lastName: cleanLastName, district, avatarUrl, mobileNumber: normalizedMobileNumber, mobileNotificationsConsent: normalizedMobileNumber ? mobileNotificationsConsent : false });
      notify(t("Profil berjaya dikemas kini", "Profile updated successfully"));
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Profil tidak dapat disimpan.", "The profile could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const switchGoogleAccount = async () => {
    setSwitchingAccount(true);
    setError("");
    try {
      await signInWithGoogle("member");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("Akaun Google tidak dapat ditukar.", "The Google account could not be switched."));
      setSwitchingAccount(false);
    }
  };

  const initials = [firstName, lastName].filter(Boolean).map((part) => part.charAt(0)).join("").slice(0, 2).toUpperCase() || "U";
  const previewUrl = photo?.previewUrl || member.avatarUrl;

  return (
    <div className="profile-editor-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
      <section className="profile-editor" role="dialog" aria-modal="true" aria-labelledby="profile-editor-title">
        <header><div><span>{t("Profil ahli", "Member profile")}</span><h2 id="profile-editor-title">{t("Kemas kini profil anda", "Update your profile")}</h2></div><button type="button" onClick={onClose} disabled={saving} aria-label={t("Tutup", "Close")}><X /></button></header>
        <form onSubmit={save}>
          {accountMismatch && <div className="profile-account-warning" role="alert"><TriangleAlert /><div><strong>{t("Akaun Google tidak sepadan", "Google account mismatch")}</strong><p>{t(`Anda sedang log masuk sebagai ${signedInEmail}. Profil ini milik ${profileEmail}.`, `You are signed in as ${signedInEmail}. This profile belongs to ${profileEmail}.`)}</p><button type="button" className="button button--ink button--small" onClick={() => { void switchGoogleAccount(); }} disabled={switchingAccount}>{switchingAccount ? <LoaderCircle className="spin" /> : <LogIn />} {switchingAccount ? t("Membuka Google…", "Opening Google…") : t("Tukar akaun Google", "Switch Google account")}</button></div></div>}
          <div className="profile-photo-field">
            <div className="profile-photo-preview" aria-label={t("Pratonton foto profil", "Profile photo preview")}>
              <span>{initials}</span>
              {previewUrl && <img src={previewUrl} alt="" />}
            </div>
            <div><strong>{t("Foto profil", "Profile photo")}</strong><p>JPEG or PNG · {t("maksimum selepas diproses", "maximum after processing")} 1 MB</p><button type="button" className="button button--ghost button--small" disabled={photoBusy || saving || accountMismatch || !authChecked} onClick={() => fileInput.current?.click()}>{photoBusy ? <LoaderCircle className="spin" /> : <Upload />} {photoBusy ? t("Mengubah saiz…", "Resizing…") : t("Pilih foto", "Choose photo")}</button><input ref={fileInput} className="sr-only" type="file" accept="image/jpeg,image/png" onChange={(event) => { void choosePhoto(event.target.files?.[0]); }} /></div>
          </div>
          <div className="profile-photo-note"><Camera /><span>{t(`Setiap foto dipotong secara automatik kepada ${AVATAR_SIDE} × ${AVATAR_SIDE} piksel. Fail melebihi ${formatBytes(MAX_AVATAR_BYTES)} akan dikecilkan sebelum dimuat naik.`, `Every photo is automatically cropped to ${AVATAR_SIDE} × ${AVATAR_SIDE} pixels. Files over ${formatBytes(MAX_AVATAR_BYTES)} are reduced before upload.`)}</span></div>
          {photo && <p className="profile-photo-result"><Check /> {t("Sedia untuk dimuat naik", "Ready to upload")}: {formatBytes(photo.originalBytes)} → {formatBytes(photo.outputBytes)}</p>}
          <div className="profile-editor-fields"><label>First Name<input value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" required maxLength={120} /></label><label>Last Name<input value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" required maxLength={120} /></label></div>
          <label>{t("Daerah di Sabah", "District in Sabah")}<select value={district} onChange={(event) => setDistrict(event.target.value)} required>{districts.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          <label>{t("Alamat e-mel", "Email address")}<input value={member.email} readOnly aria-readonly="true" /></label>
          <label>{t("Nombor mudah alih (pilihan, tidak disahkan)", "Mobile number (optional, unverified)")}<input value={mobileNumber} onChange={(event) => { setMobileNumber(event.target.value); setError(""); if (!event.target.value.trim()) setMobileNotificationsConsent(false); }} autoComplete="tel" inputMode="tel" placeholder="+60 12 345 6789" /></label>
          <label className="profile-notification-consent"><input type="checkbox" checked={mobileNotificationsConsent} disabled={!mobileNumber.trim()} onChange={(event) => setMobileNotificationsConsent(event.target.checked)} /><span><strong>{t("Benarkan pemberitahuan Kitanego", "Allow Kitanego notifications")}</strong>{t("Kitanego boleh menggunakan nombor ini untuk pemberitahuan akaun dan peluang. Anda boleh menarik balik persetujuan ini pada bila-bila masa.", "Kitanego may use this number for account and opportunity notifications. You can withdraw this consent at any time.")}</span></label>
          {error && <p className="field-error" role="alert">{error}</p>}
          <footer><button type="button" className="button button--ghost" onClick={onClose} disabled={saving}>{t("Batal", "Cancel")}</button><button type="submit" className="button button--ink" disabled={saving || photoBusy || !authChecked || accountMismatch || !firstName.trim() || !lastName.trim() || !district}>{saving ? <><LoaderCircle className="spin" /> {t("Menyimpan…", "Saving…")}</> : t("Simpan profil", "Save profile")}</button></footer>
        </form>
      </section>
    </div>
  );
}
